import { Injectable, OnDestroy } from '@angular/core';
import { EnvConfigService } from './env-config.service';
import { Link } from '@types';
import { Page } from '@bloomreach/spa-sdk';
import { replaceTokens } from '@utils/text/string-utils';
import { SignInService } from '@services/sign-in.service';
import { ProfileService } from '@services/profile.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
// NGC-14350 - Move link operations from abstract-base-component due to issues with environment tokens replacement
export class LinkService implements OnDestroy {
  public isLoggedIn: boolean;

  private unsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private envConfigService: EnvConfigService,
    private signInService: SignInService,
    private profileService: ProfileService
  ) {
    this.profileService
      .isLoggedIn$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((isLoggedIn) => {
        this.isLoggedIn = isLoggedIn;
      });
  }

  /**
   * Parse widen document json and read url
   * @param widenDocument - Widen document Json
   */

  private getWidenDocumentUrl(widenDocument: any): string {
    let url;
    if (widenDocument) {
      const widenJson = JSON.parse(widenDocument);
      url = widenJson.url;
    }
    return url;
  }

  /**
   * Return absolute link
   * @param linkCompound - Link object
   * @param cmsURL - CMS URL string
   */
  private getCmsUrl(linkCompound: Link, cmsURL: string): string {
    if (linkCompound.url.startsWith('/')) {
      // WDE-1642 : Australia Campaign Page Callout Tracking Issue
      // cmsURL gets us full domain(e.g. www.franklintempleton.com/our-products/...)
      // we just need the spaBaseUrl to be appended and if spaBaseUrl is not
      // available(In case of prod site) we just need to pass the linkCoumpond.url
      // so that the link generated is a redirection/router path and the page
      // doesn't refreshes.
      if (cmsURL === '') {
        return linkCompound.url;
      }
      return `${cmsURL}${linkCompound.url}`;
    }
    return `${cmsURL}/${linkCompound.url}`;
  }

  /**
   * Replace environment token if it is in link
   * @param linkURL - URL string
   */
  private replaceEnvToken(linkURL: string): string {
    if (/\$ENV/.test(linkURL)) {
      return replaceTokens(linkURL, this.envConfigService.getEnvConfig());
    }
    return linkURL;
  }

  /**
   * Read the link compound and return the url
   * @param page - Page
   * @param linkCompound - Link object
   * @param cmsURL - CMS URL string
   */
  public getCTALink(page: Page, linkCompound: Link, cmsURL?: string): string {
    if (linkCompound) {
      // Hide URL when link is protected and user is not logged in
      linkCompound.isLoggedIn = this.isLoggedIn;
      if (linkCompound.signInLink && !linkCompound.isLoggedIn) {
        return '#';
      }
      // Return document asset
      if (linkCompound.document) {
        return page.getContent(linkCompound.document)?.getUrl();
      }
      // Return Widen asset
      if (linkCompound.widenAssetCompound) {
        return this.getWidenDocumentUrl(
          linkCompound.widenAssetCompound?.widenDocument
        );
      }

      if (linkCompound.url) {
        // Checking if URL starts with "absolute" path or with $ENV variable.
        const regex = /^http:|^https:|^mailto:|\$ENV/;
        // Return URL defined in resourceapi
        if (regex.test(linkCompound.url)) {
          return this.replaceEnvToken(linkCompound.url);
        }
        // Adding cmsUrl to link if required
        if (cmsURL) {
          return this.getCmsUrl(linkCompound, cmsURL);
        }

        // WDE-1642 : Australia Campaign Page Callout Tracking Issue
        // cmsURL gets us full domain(e.g. www.franklintempleton.com/our-products/...)
        // we just need the spaBaseUrl to be appended and if spaBaseUrl is not
        // available(In case of prod site) we just need to pass the linkCoumpond.url
        // so that the link generated is a redirection/router path and the page
        // doesn't refreshes.
        return this.getCmsUrl(linkCompound, '');
      }
    }
  }

  /**
   * Open Sign In Modal for link compounds with sign in params
   * @param event - click event
   * @param linkCompound - linkCompound object
   */
  public openSignInLink(event: Event, linkCompound: Link): void {
    event.preventDefault();
    this.signInService.showSignInModal();
    this.signInService.showCustomIntroTxtInModal(
      linkCompound.signInIntroTextLabel
    );
  }

  /**
   * Returns true if linkCompound has sign in options
   * @param linkCompound - linkCompound object
   */
  public isLinkSignIn(linkCompound: Link): boolean {
    return !!(linkCompound?.signInLink && linkCompound?.signInIntroTextLabel);
  }

  public getHref(page: Page, linkCompound: Link, homeURL?: string): any {
    return this.getCTALink(page, linkCompound, homeURL);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
